import { LoggerService } from 'app-core/src/services/logger.service';
import { inject, singleton } from 'tsyringe';
import {
  IAppEntryPointDelegate,
  AppEntryPointService,
} from './services/app-entry-point.service';
import { TesterWindow } from './windows/tester-window';
import {
  ContentRecommendationService,
  IContentRecommendationServiceDelegate,
} from 'app-core/src/services/content-recommendation-service/content-recommendation.service';
import { TimerService } from 'app-core/src/services/timer.service';
import { OWApplicationWrapper } from './ow-odk-wrappers/ow-application-wrapper';
import {
  INotificationWindowManagerService,
  kNameOfNotificationWindowManagerService,
} from 'app-core/src/interfaces/notification-window-manager-service.interface';
import { Application } from 'app-core/src/models/application.model';
import { Game } from 'app-core/src/models/game.model';

import { ContentRecommendationUserAction } from 'app-core/src/constants/content-recommendation-user-action.enum';
import {
  kDITOWExtensionWrapper,
  kDITOWGamesWrapper,
} from 'app-core/src/constants/injectionTokens';
import { IOwGamesWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-games-wrapper.interface';
import { IOWExtensionWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-extension-wrapper.interface';
import {
  INotificationAnalyticsService,
  kNameOfNotificationAnalyticsService,
} from 'app-core/src/services/notification-analytics/notification-analytics.service';
import { NoRecommendationsReasons } from 'app-core/src/services/content-recommendation-service/no-recommendations-reasons.enums';

// Allow final logs/localStorage to be flushed
const kShutdownTimeoutForFlushingLocalStorageInMs = 100;

// -----------------------------------------------------------------------------
@singleton()
export class BackgroundController
  implements IAppEntryPointDelegate, IContentRecommendationServiceDelegate
{
  private readonly logger = LoggerService.getCategory('BackgroundController');

  // ---------------------------------------------------------------------------
  public constructor(
    private readonly entryPointService: AppEntryPointService,
    @inject(kDITOWExtensionWrapper)
    private readonly owExtensionWrapper: IOWExtensionWrapper,
    private readonly contentRecommendationService: ContentRecommendationService,
    @inject(kNameOfNotificationWindowManagerService)
    private readonly notificationWinManager: INotificationWindowManagerService,
    private readonly testerWindow: TesterWindow,
    private readonly shutdownTimer: TimerService,
    private readonly owApplicationWrapper: OWApplicationWrapper,
    @inject(kNameOfNotificationAnalyticsService)
    private readonly analyticsService: INotificationAnalyticsService,
    @inject(kDITOWGamesWrapper)
    private readonly owGamesWrapper: IOwGamesWrapper,
  ) {
    this.entryPointService.addListener(this);
    this.contentRecommendationService.addListener(this);
    this.notificationWinManager.addListener(this);
  }

  // ---------------------------------------------------------------------------
  // NOTE: Because the BackgroundController implements the AppStateController
  // interface, we cannot instantiate any states (e.g. AppStateInit) from the
  // ctor of BackgroundController.
  public async run() {
    try {
      await this.entryPointService.initialize();
      await this.printStartupInformationToConsole();
    } catch (ex) {
      this.logger.error('Error:', ex);
    }
  }

  // ---------------------------------------------------------------------------
  private async printStartupInformationToConsole() {
    await this.owExtensionWrapper.initialize();
    const { manifest } = this.owExtensionWrapper;
    this.logger.info(`App version: ${manifest?.meta?.version}`);
  }

  // ---------------------------------------------------------------------------
  private async shutdown() {
    const hasOpenWindows = await this.hasOpenWindows();
    if (hasOpenWindows) {
      this.logger.info('We have open windows - not closing app');
      return;
    }

    this.logger.info('Shutting down ...');
    this.closeBackgroundWithDelay();
  }

  // ---------------------------------------------------------------------------
  private async hasOpenWindows(): Promise<boolean> {
    const isOpen = await this.notificationWinManager.isNotificationShown();
    if (isOpen) {
      return true;
    }

    return false;
  }

  // ---------------------------------------------------------------------------
  private async closeBackgroundWithDelay() {
    await this.shutdownTimer.sleep(kShutdownTimeoutForFlushingLocalStorageInMs);
    this.owApplicationWrapper.shutdown();
  }

  // ---------------------------------------------------------------------------
  private async shouldGameBeExcluded(gameId: number) {
    try {
      const game = await this.owGamesWrapper.getGameDBInfo(gameId);

      if (
        game?.gameInfo?.TypeString === 'Application' ||
        game?.installedGameInfo?.GameInfo?.TypeString === 'Application'
      ) {
        return true;
      }

      return false;
    } catch {
      return false;
    }
  }

  // ---------------------------------------------------------------------------
  // AppEntryPointDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async onLaunchedWithDock() {
    await this.testerWindow.show();
  }

  // ---------------------------------------------------------------------------
  async onLaunchedWithGame(gameId: number) {
    const shouldExcludeGame = await this.shouldGameBeExcluded(gameId);
    if (shouldExcludeGame) {
      this.logger.info(`Excluded Game started ${gameId}, Ignoring`);
      this.shutdown();
      return;
    }

    this.contentRecommendationService.gameStarted(gameId);
  }

  // ---------------------------------------------------------------------------
  // OWGameListenerDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async onGameStarted(gameId: number) {
    const shouldExcludeGame = await this.shouldGameBeExcluded(gameId);
    if (shouldExcludeGame) {
      this.logger.info(`Excluded Game started ${gameId}, Ignoring`);
      return;
    }

    const hasOpenWindows = await this.hasOpenWindows();
    if (hasOpenWindows) {
      this.logger.info(
        'Game started while notification window is still open,' +
          ' closing notification window',
      );

      let applicationUid = '';

      const activeNotificationInfo =
        this.notificationWinManager.getNotificationInfo();

      if (activeNotificationInfo) {
        applicationUid = activeNotificationInfo.applicationUid;
      }

      this.analyticsService.reportContentRecommend(
        gameId,
        applicationUid,
        ContentRecommendationUserAction.IgnoredByLaunchingGame,
      );

      this.notificationWinManager.close();
    }

    await this.contentRecommendationService.gameStarted(gameId);
  }

  // ---------------------------------------------------------------------------
  async onGameEnded(gameId: number) {
    const shouldExcludeGame = await this.shouldGameBeExcluded(gameId);
    if (shouldExcludeGame) {
      this.logger.info(`Excluded Game Ended ${gameId}, Ignoring`);
      return;
    }

    this.contentRecommendationService.gameEnded(gameId);
  }

  // ---------------------------------------------------------------------------
  // IContentRecommendationServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  onNoRecommendations(reason: NoRecommendationsReasons) {
    // For ow we do not want to shutdown in case we are configured
    // to wait until the end
    if (reason === NoRecommendationsReasons.ConfiguredForEnd) {
      return;
    }

    this.shutdown();
  }

  // ---------------------------------------------------------------------------
  async onShowNotification(game: Game, application: Application) {
    this.notificationWinManager.show(game, application);
  }

  // ---------------------------------------------------------------------------
  // INotificationWindowManagerServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async onNotificationWindowClosed() {
    this.logger.info('Notification Window closed, Shutting down in 3 seconds');
    setTimeout(() => {
      this.shutdown();
    }, 3000);
  }
}
